import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/ui';
import { formatCents } from '@/lib/money';

/**
 * An order's lines as stock and as money. Spec 011 FR-1140 to FR-1142, spec 015
 * FR-1510 to FR-1512 and FR-1521.
 *
 * Guy, 29 October 2026: staff should see the product, the colour and the size
 * of a line in their own columns, without decoding a stock code, and see if the
 * order makes money. The code is still here, because a barcode, an import and a
 * count all use it.
 *
 * Staff only: the panel on Orders shows it and the customer's page does not.
 * Cost and margin come from the server only for somebody with costs.view. The
 * cost is the one the sale kept: the actual cost after shipping, else the
 * estimate from the day of the order. The margin is on the price without GST,
 * by the dashboard's rule. Below its target, a line or a total shows an amber
 * mark and the gap in points. Nothing is blocked.
 */
type Line = {
  id: string; title: string; item_id: string | null; code: string | null;
  product: string | null; colour: string | null; size: string | null;
  ordered: number; available: number | null;
  unit_price_cents: number; line_total_cents: number; net_cents: number;
  unit_cost_cents: number | null; cost_cents: number | null;
  cost_basis: 'actual' | 'estimate' | 'backfill' | null;
  margin_cents: number | null; margin_pct: number | null;
  target_pct: number | null; below_target_by: number | null;
};

type Total = {
  net_cents: number; cost_cents: number; profit_cents: number;
  margin_pct: number | null; target_pct: number | null; below_target_by: number | null;
  left_out: number;
} | null;

/** The amber mark: the gap in points and the target, in words as well as colour. */
function UnderTarget({ by, target }: { by: number | null; target: number | null }) {
  if (by == null || target == null) return null;
  return <Badge tone="warn" className="ml-1.5 font-sans">{by} pts under {target}%</Badge>;
}

const pct = (v: number | null) => (v == null ? null : `${v}%`);

export function OrderLines({ orderId }: { orderId: string }) {
  const q = useQuery({
    queryKey: ['admin', 'order-lines', orderId],
    queryFn: async () => {
      const res = await fetch(`/api/orders/${orderId}/lines`, { credentials: 'same-origin' });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Could not read the lines (${res.status}).`);
      }
      return (await res.json()) as {
        lines: Line[]; sees_cost: boolean; sees_stock: boolean;
        totals: { estimated: Total; actual: Total } | null;
      };
    },
  });

  if (q.isLoading) return <p className="text-[13px] text-muted">Reading the lines</p>;
  if (q.error) return <p role="alert" className="text-[13px] text-danger">{q.error.message}</p>;
  const { lines, sees_cost: seesCost, sees_stock: seesStock, totals } = q.data!;
  const dash = <span className="text-faint">&ndash;</span>;
  const th = 'px-2 py-1.5 text-left text-[10.5px] font-medium uppercase tracking-[0.06em] text-muted';
  const num = 'px-2 py-1.5 text-right font-[family-name:var(--font-mono)]';
  const backfilled = lines.some((l) => l.cost_basis === 'backfill');

  return (
    <div>
      {/* `relative`, so the wide table scrolls inside the panel and does not
          widen the page on a phone (tracker #8). */}
      <div className="relative overflow-x-auto rounded-[10px] border border-line bg-panel">
        <table className={`w-full ${seesCost ? 'min-w-[960px]' : 'min-w-[620px]'} border-collapse whitespace-nowrap text-[12.5px]`}>
          <thead>
            <tr className="border-b border-line">
              <th scope="col" className={th}>Code</th>
              <th scope="col" className={th}>Product</th>
              <th scope="col" className={th}>Colour</th>
              <th scope="col" className={th}>Size</th>
              <th scope="col" className={`${th} text-right`}>Ordered</th>
              {seesStock && <th scope="col" className={`${th} text-right`}>Available</th>}
              <th scope="col" className={`${th} text-right`}>Selling price</th>
              {seesCost && <th scope="col" className={`${th} text-right`}>Unit cost</th>}
              {seesCost && <th scope="col" className={`${th} text-right`}>Line cost</th>}
              {seesCost && <th scope="col" className={`${th} text-right`}>Gross profit</th>}
              {seesCost && <th scope="col" className={`${th} text-right`}>Margin</th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} className="border-b border-line-soft last:border-b-0">
                <td className="px-2 py-1.5 font-[family-name:var(--font-mono)]">
                  {l.item_id
                    ? <a className="underline" href={`/admin/stock?open=${l.item_id}`}>{l.code}</a>
                    : dash}
                </td>
                <td className="px-2 py-1.5">{l.product ?? l.title}</td>
                <td className="px-2 py-1.5">{l.colour ?? dash}</td>
                <td className="px-2 py-1.5">{l.size ?? dash}</td>
                <td className={num}>{l.ordered}</td>
                {seesStock && <td className={num}>{l.available ?? dash}</td>}
                <td className={num}>{formatCents(l.unit_price_cents)}</td>
                {seesCost && (
                  <td className={num}>
                    {l.unit_cost_cents != null ? formatCents(l.unit_cost_cents) : <span className="font-sans text-muted">No cost yet</span>}
                    {l.cost_basis === 'backfill' && (
                      <span className="block font-sans text-[11px] text-muted">Estimated from today&rsquo;s cost</span>
                    )}
                    {l.cost_basis === 'actual' && (
                      <span className="block font-sans text-[11px] text-muted">Actual</span>
                    )}
                  </td>
                )}
                {seesCost && <td className={num}>{l.cost_cents != null ? formatCents(l.cost_cents) : dash}</td>}
                {seesCost && <td className={num}>{l.margin_cents != null ? formatCents(l.margin_cents) : dash}</td>}
                {seesCost && (
                  <td className={num}>
                    {pct(l.margin_pct) ?? dash}
                    <UnderTarget by={l.below_target_by} target={l.target_pct} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {seesCost && totals && (
        <div className="mt-3 space-y-2 text-[12.5px]">
          {!totals.estimated && !totals.actual && (
            <p className="text-muted">No line has a cost, so there is no margin.</p>
          )}
          <OrderTotal label="Estimated" total={totals.estimated} />
          <OrderTotal label="Actual" total={totals.actual} />
          {backfilled && (
            <p className="text-[12px] text-muted">
              This order is older than the cost record. Its estimate uses the cost the item has
              today, not the cost on the day of the sale.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** One total block: sales without GST, cost, gross profit and margin. FR-1511. */
function OrderTotal({ label, total }: { label: string; total: Total }) {
  if (!total) return null;
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-[8px] bg-raised px-3 py-2">
      <strong className="w-[72px] text-ink">{label}</strong>
      <span>Sales without GST <b className="font-[family-name:var(--font-mono)]">{formatCents(total.net_cents)}</b></span>
      <span>Cost <b className="font-[family-name:var(--font-mono)]">{formatCents(total.cost_cents)}</b></span>
      <span>Gross profit <b className="font-[family-name:var(--font-mono)]">{formatCents(total.profit_cents)}</b></span>
      <span>
        Margin <b className="font-[family-name:var(--font-mono)]">{pct(total.margin_pct) ?? '–'}</b>
        <UnderTarget by={total.below_target_by} target={total.target_pct} />
      </span>
      {total.left_out > 0 && (
        <span className="text-muted">
          {total.left_out} {total.left_out === 1 ? 'line has' : 'lines have'} no cost and {total.left_out === 1 ? 'is' : 'are'} not in this total.
        </span>
      )}
    </div>
  );
}
